import {
    FormControlLabel, Grid, Radio, RadioGroup
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import { SubmitButton } from '@tassat/button';
import { getPledgeDeclineReasons, pledgeTransactionTypeCodeToEnum } from '@tassat/common-definitions';
import { ActionModal } from '@tassat/modal';
import React from 'react';

import { ApplicationMuiTheme } from 'Lib/themes/muiTheme';
import Worker from 'Lib/worker/PrimusWorkerWrapper';
import { useAppSelector } from 'Reducers/store/hooks';
import { getPledgeAccountDetail } from 'Selectors/accounts';
import { getPledgeDetail, getUser } from 'Selectors/common';

import { PledgeContextWorkflowContext } from '../../pledgeContextWorkflow';

const useStyles = makeStyles((theme: ApplicationMuiTheme) => ({
    root: {
        minWidth: '560px',
        padding: '1.5rem'
    },
    promptText: {
        fontSize: '1.5rem',
        padding: '1rem 0'
    },
    radioGroup: {
        width: '100%',
        marginBottom: '2rem'
    },
    radioContainer: { margin: '0 auto' },
    radioColumn: { width: 'auto' as const },
    radio: {
        color: theme?.app?.applicationColors.enumColors.greyLight ||
            '#a7a7a7',
        '&.Mui-checked': {
            color: theme?.app?.applicationColors.failureDark ||
                '#AD3D4B'
        }
    },
    checked: {},
    cancelButton: {
        cursor: 'pointer' as const,
        fontSize: '1.2rem',
        lineHeight: '1.2rem',
        color: theme?.app?.clientTheme.colors.primaryColor ||
            '#375073',
        padding: '.3rem',
        marginTop: '1rem',
        '&:hover': {
            backgroundColor: theme?.app?.clientTheme.colors.buttons.primaryHover ||
                '#bec4cf'
        }
    }
}));

function DeclinePledgeComponent() {
    const classes = useStyles();

    const { pledgeContextWorkflowState, dispatchPledgeContextWorkflowAction } = React.useContext(PledgeContextWorkflowContext)!;
    const { modalScreen, selectedPledgeTransaction, selectedPledge } = pledgeContextWorkflowState;
    const isCancel = modalScreen === 'cancel';
    const [ declineReason, setDeclineReason ] = React.useState<string>('');

    const currentUser = useAppSelector(getUser);
    const pledgeDetail = useAppSelector(getPledgeDetail);
    const pledgeAccountDetail = useAppSelector((state) => getPledgeAccountDetail(state, pledgeDetail.PledgeId, pledgeDetail));

    // Split reasons in two for two column
    const [ leftCol, rightCol ] = React.useMemo(() => {
        const pledgeDeclineReasons = getPledgeDeclineReasons();
        const midIndex = Math.ceil(pledgeDeclineReasons.length / 2);
        const leftCol = pledgeDeclineReasons.slice(0, midIndex);
        const rightCol = pledgeDeclineReasons.slice(midIndex, pledgeDeclineReasons.length);

        return [ leftCol, rightCol ];
    }, []);

    const handleClose = () => {
        dispatchPledgeContextWorkflowAction({
            type: 'CHANGE_MODAL_SCREEN',
            payload: { screen: 'details' }
        });
    };

    const handleCancel = async () => {
        if (!selectedPledgeTransaction || !currentUser) return;
        try {
            const { PledgeTransactionRequestId, PledgeTransactionType } = selectedPledgeTransaction;
            const transactionTypeEnum = pledgeTransactionTypeCodeToEnum(PledgeTransactionType);
            if (!transactionTypeEnum) return;

            await Worker.getInstance()
                .cancelPledgeTransaction({
                    TransactionRequestId: PledgeTransactionRequestId,
                    CurrentUser: currentUser,
                    TransactionType: PledgeTransactionType,
                    AccountId: pledgeAccountDetail.AccountId,
                    Reason: declineReason,
                    PledgeId: selectedPledge.PledgeId
                });

            dispatchPledgeContextWorkflowAction({
                type: 'SET_MODAL_PLEDGE_VALUES',
                payload: {
                    RequestId: selectedPledgeTransaction.PledgeTransactionRequestId,
                    AssetQuantity: selectedPledgeTransaction.PledgeTransactionAmount,
                    Ticker: selectedPledge?.Ticker,
                    ReceivingAccountName: pledgeDetail.PledgeParticipant[0]?.PledgeParticipantAccountName
                }
            });
            dispatchPledgeContextWorkflowAction({
                type: 'SET_MODAL_ACTION',
                payload: 'cancel'
            });
            dispatchPledgeContextWorkflowAction({
                type: 'SET_PLEDGE_TYPE',
                payload: transactionTypeEnum
            });
            dispatchPledgeContextWorkflowAction({
                type: 'SET_PLEDGE_ACTION_IN_PROGRESS',
                payload: true
            });
            dispatchPledgeContextWorkflowAction({
                type: 'CHANGE_MODAL_SCREEN',
                payload: { screen: 'details' }
            });
        } catch (error) {
            console.error(error);
        }
    };

    const handleDecline = async () => {
        if (!selectedPledgeTransaction || !currentUser) return;
        try {
            const { PledgeTransactionRequestId, PledgeTransactionType } = selectedPledgeTransaction;
            const transactionTypeEnum = pledgeTransactionTypeCodeToEnum(PledgeTransactionType);
            if (!transactionTypeEnum) return;

            await Worker.getInstance()
            .rejectTransaction({
                TransactionRequestId: PledgeTransactionRequestId,
                CurrentUser: currentUser,
                TransactionType: PledgeTransactionType,
                AccountId: pledgeAccountDetail.AccountId,
                Reason: declineReason
            });

            dispatchPledgeContextWorkflowAction({
                type: 'SET_MODAL_PLEDGE_VALUES',
                payload: {
                    RequestId: selectedPledgeTransaction.PledgeTransactionRequestId,
                    AssetQuantity: selectedPledgeTransaction.PledgeTransactionAmount,
                    Ticker: selectedPledge?.Ticker,
                    ReceivingAccountName: pledgeDetail.PledgeParticipant[0]?.PledgeParticipantAccountName
                }
            });
            dispatchPledgeContextWorkflowAction({
                type: 'SET_MODAL_ACTION',
                payload: 'decline'
            });
            dispatchPledgeContextWorkflowAction({
                type: 'SET_PLEDGE_TYPE',
                payload: transactionTypeEnum
            });
            dispatchPledgeContextWorkflowAction({
                type: 'SET_PLEDGE_ACTION_IN_PROGRESS',
                payload: true
            });
            dispatchPledgeContextWorkflowAction({
                type: 'CHANGE_MODAL_SCREEN',
                payload: { screen: 'details' }
            });
        } catch (error) {
            console.error(error);
        }
    };

    return (
        <ActionModal
            headerStyle='decline'
            onClose={handleClose}
            open={modalScreen === 'decline' || modalScreen === 'cancel'}
            title={modalScreen.toUpperCase()}
        >
            <Grid
                alignItems='center'
                className={classes.root}
                container
                direction='column'
                spacing={0}
                wrap='nowrap'
            >
                <>
                    <span className={classes.promptText}>Select a reason for {isCancel ? 'canceling' : 'declining'} this post:</span>
                    <RadioGroup
                        aria-label='decline-reason'
                        className={classes.radioGroup}
                        name='decline-reason'
                        onChange={(e) => setDeclineReason(e.target.value)}
                        value={declineReason}
                    >
                        <Grid
                            className={classes.radioContainer}
                            container
                            direction='row'
                            item
                            justifyContent='space-around'
                            spacing={0}
                            wrap='nowrap'
                            xs={8}
                        >
                            <Grid
                                className={classes.radioColumn}
                                container
                                direction='column'
                                item
                                spacing={0}
                                wrap='nowrap'
                            >
                                {leftCol.map(declineReason => (
                                    <FormControlLabel
                                        control={<Radio classes={{
                                                root: classes.radio,
                                                checked: classes.checked
                                            }}
                                        />}
                                        key={declineReason.code}
                                        label={declineReason.reason}
                                        value={declineReason.code}
                                    />
                                    ))}
                            </Grid>
                            <Grid
                                className={classes.radioColumn}
                                container
                                direction='column'
                                item
                                spacing={0}
                                wrap='nowrap'
                            >
                                {rightCol.map(declineReason => (
                                    <FormControlLabel
                                        control={<Radio classes={{
                                                root: classes.radio,
                                                checked: classes.checked
                                            }}
                                        />}
                                        key={declineReason.code}
                                        label={declineReason.reason}
                                        value={declineReason.code}
                                    />
                                    ))}
                            </Grid>
                        </Grid>
                    </RadioGroup>
                </>
                <SubmitButton
                    disabled={!declineReason}
                    onClick={() => {
                        if (isCancel) {
                            handleCancel();
                        } else {
                            handleDecline();
                        }
                    }}
                    type='submit'
                />
                <span
                    className={classes.cancelButton}
                    onClick={handleClose}
                >
                    CANCEL
                </span>
            </Grid>
        </ActionModal>
    );
}

export { DeclinePledgeComponent as Decline };